// Attorney Dashboard Page
// BP-10-04: Attorney Frontend

import { Link } from 'react-router-dom';
import {
  Briefcase,
  Plus,
  AlertTriangle,
  Calendar,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import { useAttorneyProfile, useCases, useDashboardStats } from '@/features/attorney/hooks/useAttorney';
import { CaseSummaryStats, CaseCard, CaseStatusBadge } from '../components';
import type { CaseStatus } from '@/features/attorney/types/attorney';

export function AttorneyDashboardPage() {
  const { data: profile } = useAttorneyProfile();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: recentCases, isLoading: casesLoading } = useCases({ 
    limit: 5,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  const defaultStats = {
    totalCases: 0,
    activeCases: 0,
    casesInDiscovery: 0,
    casesInSettlement: 0,
    closedCasesThisYear: 0,
    totalSettlementsThisYear: 0,
    averageSettlement: 0,
    upcomingDeadlines: 0,
    clientEngagementRate: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-navy">
            Welcome back{profile?.user ? `, ${profile.user.firstName}` : ''}
          </h1>
          <p className="text-gray-600">
            Here's an overview of your case portfolio
          </p>
        </div>
        <Link
          to="/attorney/cases/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Case
        </Link>
      </div>

      {/* Stats Grid */}
      <CaseSummaryStats stats={stats || defaultStats} isLoading={statsLoading} />

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Cases */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-navy flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-sage" />
              Recent Cases
            </h2>
            <Link
              to="/attorney/cases"
              className="text-sm text-sage hover:text-sage-dark flex items-center gap-1"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="divide-y divide-gray-100">
            {casesLoading ? (
              [1, 2, 3].map((i) => (
                <div key={i} className="p-4 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              ))
            ) : recentCases?.items.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No cases yet</p>
                <Link
                  to="/attorney/cases/new"
                  className="text-sage hover:text-sage-dark mt-2 inline-block"
                >
                  Create your first case
                </Link>
              </div>
            ) : (
              recentCases?.items.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/attorney/cases/${caseItem.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-navy truncate">
                        {caseItem.family?.name || 'Unknown Family'}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">
                        {caseItem.caseNumber || caseItem.internalReference}
                        {caseItem.defendant && ` • vs ${caseItem.defendant}`}
                      </p>
                    </div>
                    <CaseStatusBadge status={caseItem.status as CaseStatus} />
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Sidebar - Upcoming & Alerts */}
        <div className="space-y-6">
          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-navy">Upcoming Deadlines</h3>
            </div>
            <div className="p-4">
              {stats?.upcomingDeadlines === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No upcoming deadlines
                </p>
              ) : (
                <div className="space-y-3">
                  {/* Placeholder - would be populated with real deadline data */}
                  <div className="flex items-start gap-3 p-2 bg-amber-50 rounded-lg">
                    <div className="w-8 h-8 bg-amber-100 rounded flex items-center justify-center text-xs font-semibold text-amber-700">
                      15
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        Discovery Response Due
                      </p>
                      <p className="text-xs text-gray-500">Martinez Case</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-xs font-semibold text-gray-700">
                      22
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        Mediation Scheduled
                      </p>
                      <p className="text-xs text-gray-500">Thompson Case</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-navy">Quick Actions</h3>
            </div>
            <div className="p-2">
              <Link
                to="/attorney/cases/new"
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create New Case
              </Link>
              <Link
                to="/attorney/cases?status=discovery"
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <AlertTriangle className="w-4 h-4" />
                View Discovery Cases
              </Link>
              <Link
                to="/attorney/analytics"
                className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                View Analytics
              </Link>
            </div>
          </div>

          {/* Verification Status */}
          {profile && !profile.isVerified && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">
                    Complete Verification
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Verify your attorney credentials to unlock all features.
                  </p>
                  <Link
                    to="/attorney/profile"
                    className="text-sm font-medium text-amber-800 hover:text-amber-900 mt-2 inline-block"
                  >
                    Complete verification →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttorneyDashboardPage;


